// Histórico de manutenções (P-16).
//
// A aba era um cartão vazio: "Histórico de manutenções por torre e item", sem
// definição do que mostrar nem de onde os dados viriam. Confirmado com o
// produto: histórico por item de infraestrutura.
//
// O registro vive em `manutencoes` (migration 0031), e não no jsonb
// `infraestrutura.historico`, que existia desde o baseline sem nunca receber uma
// linha. `infraestrutura.ultima_manutencao` virou coluna derivada: o gatilho a
// mantém igual ao registro mais recente, inclusive quando um registro é
// excluído e a data precisa voltar para a anterior.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useCatalogo, classesDaCategoria, type TipoDeManutencao } from '@/lib/catalogo';
import { hojeISO } from '@/lib/datas';
import {
  enviarDocumento,
  excluirManutencao,
  gravarManutencao,
  lerManutencoes,
  removerDocumento,
  urlDoDocumento,
  type AnexoDeDocumento,
  type Manutencao,
} from '@/lib/condominioRepo';

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

const formatData = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const fmtCentavos = (v: number | null) =>
  v === null ? '—' : (v / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** "1.234,56" → 123456. Aceita o que a pessoa digitar, com ou sem separador. */
function paraCentavos(texto: string): number | null {
  const limpo = texto
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.');
  if (!limpo) return null;
  const valor = Number(limpo);
  if (Number.isNaN(valor) || valor < 0) return null;
  return Math.round(valor * 100);
}

interface ItemDeInfra {
  id: string;
  nome: string;
  localizacao: string | null;
}

interface Formulario {
  id?: string;
  infraestruturaId: string;
  data: string;
  tipoId: string;
  descricao: string;
  responsavel: string;
  prestadorId: string;
  custo: string;
  observacoes: string;
  anexos: AnexoDeDocumento[];
}

const formularioVazio = (): Formulario => ({
  infraestruturaId: '',
  data: hojeISO(),
  tipoId: '',
  descricao: '',
  responsavel: '',
  prestadorId: '',
  custo: '',
  observacoes: '',
  anexos: [],
});

const fmtTamanho = (bytes: number) => {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`;
};

const iconeDoArquivo = (nome: string) =>
  /\.(jpe?g|png|webp)$/i.test(nome) ? 'ri-image-line' : 'ri-file-text-line';

export default function ManutencoesTab({ condoId }: { condoId: string }) {
  const { itens: tipos } = useCatalogo<TipoDeManutencao>('manutencao_tipos');

  const [manutencoes, setManutencoes] = useState<Manutencao[]>([]);
  const [itens, setItens] = useState<ItemDeInfra[]>([]);
  const [prestadores, setPrestadores] = useState<{ id: string; nome: string }[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [filtroItem, setFiltroItem] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);
  const [excluindo, setExcluindo] = useState<Manutencao | null>(null);

  const carregar = useCallback(async () => {
    if (!condoId) return;
    setCarregando(true);
    setErro(null);
    try {
      const [registros, infra, prest] = await Promise.all([
        lerManutencoes(condoId),
        supabase
          .from('infraestrutura')
          .select('id, nome, localizacao')
          .eq('condominio_id', condoId)
          .order('nome'),
        supabase.from('prestadores').select('id, nome').eq('ativo', true).order('nome'),
      ]);

      if (infra.error) throw new Error(infra.error.message);

      setManutencoes(registros);
      setItens((infra.data ?? []) as ItemDeInfra[]);
      // Prestador é opcional: falhar a lista inteira porque o select de
      // prestador não carregou seria trocar um campo por uma tela.
      setPrestadores(prest.error ? [] : ((prest.data ?? []) as { id: string; nome: string }[]));
    } catch (e: any) {
      setErro(e?.message || 'Erro ao carregar as manutenções.');
    } finally {
      setCarregando(false);
    }
  }, [condoId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const tipoPorId = useMemo(() => {
    const mapa: Record<string, TipoDeManutencao> = {};
    for (const t of tipos) mapa[t.id] = t;
    return mapa;
  }, [tipos]);

  const filtradas = manutencoes.filter(
    (m) =>
      (!filtroItem || m.infraestruturaId === filtroItem) &&
      (!filtroTipo || m.tipoId === filtroTipo),
  );

  const totais = useMemo(() => {
    const custo = filtradas.reduce((s, m) => s + (m.custoCentavos ?? 0), 0);
    const doAno = filtradas.filter(
      (m) => m.data.slice(0, 4) === String(new Date().getFullYear()),
    ).length;
    return {
      registros: filtradas.length,
      custo,
      doAno,
      itens: new Set(filtradas.map((m) => m.infraestruturaId)).size,
    };
  }, [filtradas]);

  const abrirNova = () => {
    setFormulario({ ...formularioVazio(), tipoId: tipos[0]?.id ?? '' });
    setErroFormulario(null);
  };

  const abrirEdicao = (m: Manutencao) => {
    setFormulario({
      id: m.id,
      infraestruturaId: m.infraestruturaId,
      data: m.data.slice(0, 10),
      tipoId: m.tipoId,
      descricao: m.descricao,
      responsavel: m.responsavel ?? '',
      prestadorId: m.prestadorId ?? '',
      custo: m.custoCentavos === null ? '' : (m.custoCentavos / 100).toFixed(2).replace('.', ','),
      observacoes: m.observacoes ?? '',
      anexos: m.anexos,
    });
    setErroFormulario(null);
  };

  const validar = (f: Formulario): string | null => {
    if (!f.infraestruturaId) return 'Escolha o item de infraestrutura.';
    if (!f.tipoId) return 'Escolha o tipo de manutenção.';
    if (!f.descricao.trim()) return 'Descreva o que foi feito.';
    if (f.data > hojeISO()) return 'A data não pode ser futura: agendamento não é histórico.';
    if (f.custo.trim() && paraCentavos(f.custo) === null) return 'Custo inválido.';
    return null;
  };

  /** Grava e devolve o id — o anexo precisa dele para montar o caminho. */
  const gravar = (f: Formulario, anexos: AnexoDeDocumento[]) =>
    gravarManutencao({
      id: f.id,
      condominioId: condoId,
      infraestruturaId: f.infraestruturaId,
      data: f.data,
      tipoId: f.tipoId,
      descricao: f.descricao.trim(),
      responsavel: f.responsavel.trim() || null,
      prestadorId: f.prestadorId || null,
      custoCentavos: f.custo.trim() ? paraCentavos(f.custo) : null,
      observacoes: f.observacoes.trim() || null,
      anexos,
    });

  const salvar = async () => {
    if (!formulario) return;

    const erroValidacao = validar(formulario);
    if (erroValidacao) {
      setErroFormulario(erroValidacao);
      return;
    }

    setSalvando(true);
    setErroFormulario(null);
    try {
      await gravar(formulario, formulario.anexos);
      setFormulario(null);
      await carregar();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Anexa o arquivo.
   *
   * O registro é gravado antes do upload: o caminho no bucket é
   * `<condominio>/<manutencao>/<arquivo>`, e uma manutenção nova ainda não tem
   * id. Gravar depois deixaria arquivo órfão no storage se algo falhasse no
   * meio — e órfão em bucket privado ninguém encontra para limpar.
   */
  const anexarArquivo = async (arquivo: File) => {
    if (!formulario) return;

    const erroValidacao = validar(formulario);
    if (erroValidacao) {
      setErroFormulario(erroValidacao);
      return;
    }

    setEnviandoArquivo(true);
    setErroFormulario(null);
    try {
      const id = await gravar(formulario, formulario.anexos);
      const anexo = await enviarDocumento('manutencoes', condoId, id, arquivo);
      const anexos = [...formulario.anexos, anexo];

      const comId = { ...formulario, id, anexos };
      await gravar(comId, anexos);
      setFormulario(comId);
      await carregar();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível anexar o arquivo.');
    } finally {
      setEnviandoArquivo(false);
    }
  };

  const removerAnexo = async (anexo: AnexoDeDocumento) => {
    if (!formulario?.id) return;
    setErroFormulario(null);
    try {
      await removerDocumento('manutencoes', anexo.caminho);
      const anexos = formulario.anexos.filter((a) => a.caminho !== anexo.caminho);
      const semAnexo = { ...formulario, anexos };
      await gravar(semAnexo, anexos);
      setFormulario(semAnexo);
      await carregar();
    } catch (e: any) {
      setErroFormulario(e?.message || 'Não foi possível remover o anexo.');
    }
  };

  const abrirAnexo = async (anexo: AnexoDeDocumento) => {
    const url = await urlDoDocumento('manutencoes', anexo.caminho);
    if (url) window.open(url, '_blank', 'noopener');
    else setErroFormulario('Não foi possível abrir o arquivo.');
  };

  const confirmarExclusao = async () => {
    if (!excluindo) return;
    try {
      await excluirManutencao(excluindo.id);
      setExcluindo(null);
      await carregar();
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível excluir.');
      setExcluindo(null);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span className="text-sm">Carregando manutenções...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-foreground-900">Manutenções</h3>
          <p className="text-sm text-foreground-500 mt-0.5">
            Histórico por item de infraestrutura. A data da última manutenção do item vem daqui.
          </p>
        </div>
        <button
          type="button"
          onClick={abrirNova}
          disabled={itens.length === 0}
          className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className="ri-add-line"></i>Registrar manutenção
        </button>
      </div>

      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-600"></i>
          <span className="text-sm text-red-700">{erro}</span>
        </div>
      )}

      {itens.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-tools-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">
            Nenhum item de infraestrutura cadastrado
          </p>
          <p className="text-sm text-foreground-500 mt-1">
            A manutenção é registrada sobre um item. Cadastre elevador, portão, bomba ou gerador na
            aba{' '}
            <Link
              to={`/condominios/${condoId}/infraestrutura`}
              className="text-primary-600 hover:underline"
            >
              Infraestrutura
            </Link>{' '}
            primeiro.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Registros', valor: String(totais.registros) },
              { label: 'Itens atendidos', valor: String(totais.itens) },
              { label: 'Neste ano', valor: String(totais.doAno) },
              { label: 'Custo somado', valor: fmtCentavos(totais.custo) },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-background-50 border border-background-200/70 rounded-lg p-4"
              >
                <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                <p className="text-xl font-bold mt-1 text-foreground-900">{card.valor}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <select
              aria-label="Filtrar por item"
              value={filtroItem}
              onChange={(e) => setFiltroItem(e.target.value)}
              className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="">Todos os itens</option>
              {itens.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nome}
                </option>
              ))}
            </select>
            <select
              aria-label="Filtrar por tipo"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="">Todos os tipos</option>
              {tipos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            {filtradas.length === 0 ? (
              <div className="p-10 text-center">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-history-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-foreground-700">
                  Nenhuma manutenção registrada
                </p>
                <p className="text-sm text-foreground-500 mt-1">
                  O primeiro registro define a data da última manutenção do item.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Data
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3">
                        Item
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3">
                        Tipo
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 hidden md:table-cell">
                        Responsável
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Custo
                      </th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/50">
                    {filtradas.map((m) => {
                      const tipo = tipoPorId[m.tipoId];
                      return (
                        <tr key={m.id} className="hover:bg-background-100/30 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-sm text-foreground-700 whitespace-nowrap">
                              {formatData(m.data)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground-800">
                              {m.infraestruturaNome}
                            </p>
                            <p className="text-xs text-foreground-500 truncate max-w-[280px]">
                              {m.descricao}
                              {m.anexos.length > 0 && (
                                <span className="ml-2 whitespace-nowrap">
                                  <i className="ri-attachment-2"></i> {m.anexos.length}
                                </span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${classesDaCategoria(tipo?.cor)}`}
                            >
                              {tipo?.icone && <i className={`${tipo.icone} text-[11px]`}></i>}
                              {tipo?.nome ?? m.tipoId}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-sm text-foreground-600">
                              {m.prestadorNome || m.responsavel || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm text-foreground-800 whitespace-nowrap">
                              {fmtCentavos(m.custoCentavos)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => abrirEdicao(m)}
                                title="Editar manutenção"
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                              >
                                <i className="ri-edit-line text-sm"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => setExcluindo(m)}
                                title="Excluir manutenção"
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Formulário */}
      {formulario && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[6vh] px-4 pb-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setFormulario(null)}
            aria-hidden="true"
          ></div>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-manutencao"
            className="relative bg-background-50 rounded-lg shadow-xl w-full max-w-xl max-h-[88vh] overflow-y-auto"
          >
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h3 id="titulo-manutencao" className="text-base font-semibold text-foreground-900">
                {formulario.id ? 'Editar manutenção' : 'Registrar manutenção'}
              </h3>
              <button
                type="button"
                onClick={() => setFormulario(null)}
                aria-label="Fechar"
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="man-item"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Item de infraestrutura
                  </label>
                  <select
                    id="man-item"
                    value={formulario.infraestruturaId}
                    onChange={(e) =>
                      setFormulario({ ...formulario, infraestruturaId: e.target.value })
                    }
                    className={CLASSE_CAMPO}
                  >
                    <option value="">Selecione...</option>
                    {itens.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.nome}
                        {i.localizacao ? ` — ${i.localizacao}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="man-data"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Data
                  </label>
                  <input
                    id="man-data"
                    type="date"
                    max={hojeISO()}
                    value={formulario.data}
                    onChange={(e) => setFormulario({ ...formulario, data: e.target.value })}
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="man-tipo"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Tipo
                  </label>
                  <select
                    id="man-tipo"
                    value={formulario.tipoId}
                    onChange={(e) => setFormulario({ ...formulario, tipoId: e.target.value })}
                    className={CLASSE_CAMPO}
                  >
                    <option value="">Selecione...</option>
                    {tipos.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="man-descricao"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    O que foi feito
                  </label>
                  <input
                    id="man-descricao"
                    type="text"
                    value={formulario.descricao}
                    onChange={(e) => setFormulario({ ...formulario, descricao: e.target.value })}
                    placeholder="Troca de cabo de aço, revisão trimestral..."
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="man-prestador"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Prestador
                    <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
                  </label>
                  <select
                    id="man-prestador"
                    value={formulario.prestadorId}
                    onChange={(e) => setFormulario({ ...formulario, prestadorId: e.target.value })}
                    className={CLASSE_CAMPO}
                  >
                    <option value="">Nenhum</option>
                    {prestadores.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="man-responsavel"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Responsável
                    <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
                  </label>
                  <input
                    id="man-responsavel"
                    type="text"
                    value={formulario.responsavel}
                    onChange={(e) => setFormulario({ ...formulario, responsavel: e.target.value })}
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="man-custo"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Custo (R$)
                    <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
                  </label>
                  <input
                    id="man-custo"
                    type="text"
                    inputMode="decimal"
                    value={formulario.custo}
                    onChange={(e) => setFormulario({ ...formulario, custo: e.target.value })}
                    placeholder="1.250,00"
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="man-obs"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Observações
                    <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
                  </label>
                  <textarea
                    id="man-obs"
                    rows={2}
                    value={formulario.observacoes}
                    onChange={(e) => setFormulario({ ...formulario, observacoes: e.target.value })}
                    className={`${CLASSE_CAMPO} resize-y`}
                  />
                </div>
              </div>

              {/* Anexos */}
              <div className="pt-4 border-t border-background-200/70">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">Anexos</h4>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      Nota fiscal, laudo da inspeção, foto do antes e do depois.
                    </p>
                  </div>
                  <label
                    className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium transition-colors ${
                      enviandoArquivo
                        ? 'opacity-60 cursor-not-allowed text-foreground-400'
                        : 'text-foreground-700 hover:bg-background-100 cursor-pointer'
                    }`}
                  >
                    <i
                      className={
                        enviandoArquivo ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'
                      }
                    ></i>
                    {enviandoArquivo ? 'Enviando...' : 'Anexar'}
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={enviandoArquivo}
                      onChange={(e) => {
                        const arquivo = e.target.files?.[0];
                        e.target.value = '';
                        if (arquivo) void anexarArquivo(arquivo);
                      }}
                    />
                  </label>
                </div>

                {formulario.anexos.length === 0 ? (
                  <p className="text-sm text-foreground-500">
                    Nenhum anexo. PDF, JPG, PNG ou WEBP de até 10 MB.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {formulario.anexos.map((anexo) => (
                      <li
                        key={anexo.caminho}
                        className="flex items-center justify-between gap-3 rounded-md border border-background-200/70 px-3 py-2"
                      >
                        <button
                          type="button"
                          onClick={() => void abrirAnexo(anexo)}
                          className="flex items-center gap-2 min-w-0 text-left"
                        >
                          <i className={`${iconeDoArquivo(anexo.nome)} text-foreground-500`}></i>
                          <span className="text-sm text-foreground-800 truncate hover:text-primary-600">
                            {anexo.nome}
                          </span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">
                            {fmtTamanho(anexo.tamanho)}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => void removerAnexo(anexo)}
                          aria-label={`Remover ${anexo.nome}`}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {erroFormulario && (
                <p className="text-sm text-red-700 flex items-start gap-1.5">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {erroFormulario}
                </p>
              )}
            </div>

            <div className="px-5 py-4 border-t border-background-200/70 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setFormulario(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvar}
                disabled={salvando}
                className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {excluindo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setExcluindo(null)}
            aria-hidden="true"
          ></div>
          <div
            role="dialog"
            aria-modal="true"
            className="relative bg-background-50 rounded-lg shadow-xl w-full max-w-md p-5"
          >
            <h3 className="text-base font-semibold text-foreground-900 mb-2">Excluir manutenção</h3>
            <p className="text-sm text-foreground-600 mb-5">
              Excluir o registro de {formatData(excluindo.data)} em{' '}
              <span className="font-medium">{excluindo.infraestruturaNome}</span>? A data da última
              manutenção do item volta para o registro anterior.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setExcluindo(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarExclusao}
                className="px-4 py-2 rounded-md bg-red-600 text-background-50 text-sm font-semibold hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
